import { environment } from 'environments/environment';

/**
 * Sanitizes internal URLs by removing language-specific paths and trailing slashes
 *
 * Rules:
 * - Only processes internal URLs (URLs that do NOT start with http:// or https://)
 * - External URLs (starting with http/https) are returned unchanged
 * - Removes "/he/" from the URL path
 * - Removes trailing "/" from the URL path (but preserves root path "/")
 *
 * @param url The URL to sanitize
 * @returns The sanitized URL
 *
 * @example
 * sanitizeInternalUrl('/he/page') // '/page'
 * sanitizeInternalUrl('he/page') // 'page'
 * sanitizeInternalUrl('he/tel-hashomer/departments/') // 'tel-hashomer/departments'
 * sanitizeInternalUrl('/page/') // '/page'
 * sanitizeInternalUrl('/he/page/') // '/page'
 * sanitizeInternalUrl('/') // '/'
 * sanitizeInternalUrl('https://example.com/he/page') // 'https://example.com/he/page' (unchanged - external)
 */
export function sanitizeInternalUrl(url: string | null | undefined): string {
  if (!url) {
    return '';
  }

  // Skip external URLs - return them unchanged
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  // Split URL into path and query/fragment parts
  const hashIndex = url.indexOf('#');
  const queryIndex = url.indexOf('?');

  let path = url;
  let query = '';
  let fragment = '';

  // Extract fragment if present
  if (hashIndex !== -1) {
    fragment = url.substring(hashIndex);
    path = url.substring(0, hashIndex);
  }

  // Extract query if present
  if (queryIndex !== -1 && (hashIndex === -1 || queryIndex < hashIndex)) {
    const endIndex = hashIndex !== -1 ? hashIndex : url.length;
    query = url.substring(queryIndex, endIndex);
    path = url.substring(0, queryIndex);
    fragment = hashIndex !== -1 ? url.substring(hashIndex) : '';
  }

  // Remove /he/ from the path (case-insensitive)
  // Handle both "/he/" in the middle and "he/" at the start
  path = path.replace(/^he\//i, '');  // Remove "he/" at the start
  path = path.replace(/\/he\//gi, '/');  // Remove "/he/" in the middle

  // Remove trailing slash from path, but keep it if it's just the root path "/"
  if (path.length > 1 && path.endsWith('/')) {
    path = path.slice(0, -1);
  }

  return path + query + fragment;
}

/** Converts an internal path to an absolute site URL for JSON-LD. */
export function toAbsoluteSiteUrl(url: string | null | undefined): string {
  if (!url) {
    return '';
  }

  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  const origin = (environment.clientUrl || window.location.origin).replace(/\/$/, '');

  return url.startsWith('/') ? `${origin}${url}` : `${origin}/${url}`;
}
